import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay, Stack,
  Text
} from "@chakra-ui/react";
import BigNumber from "bignumber.js";
import { addWeeks, format } from "date-fns";
import React, { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NftResource } from "../../../components/NftResource";
import { colors } from "../../../core/constants/colors";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { PAYMENT_STRATEGY } from "../../../core/constants/paymentStrategy";
import { IMoralisNft } from "../../../core/models/nft";
import { Quote } from "../../../core/models/quote";
import { formatNumber } from "../../../core/utils/number";
import { useAppSelector } from "../../../store/hook";

interface Props {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  nft: IMoralisNft;
  quote: Quote;
  paymentStrategy: PAYMENT_STRATEGY | null;
}

export const CompletePaymentModal = React.memo((props: Props) => {
  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  const { t } = useTranslation([I18N_NAMESPACE.ASSET_DETAIL]);

  const token = useMemo(
    () =>
      props.quote?.take?.asset_type?.token_address
        ? supportedTokens.byId[props.quote?.take?.asset_type?.token_address]
        : null,
    [props.quote.take.asset_type.token_address]
  );
  const price = useMemo(() => props.quote.take.value || 0, [props.quote.take.value]);
  const paymentPeriods = useMemo(() => (!!price ? Math.ceil(price / Math.ceil(price / 4)) : 0), [price]);
  return (
    <Modal isOpen={props.isVisible} onClose={props.onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Complete Payment")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={7}>
            <Flex justifyContent="center" alignItems="center">
              <Box borderWidth="1px" borderRadius="lg" height="80px" width="80px">
                <NftResource image={props.nft.metadata?.image} animationUrl={props.nft.metadata?.animation_url} muted />
              </Box>

              <Flex flex="1" marginLeft="16px" justifyContent="space-between" alignItems={"center"}>
                <Box>
                  <Box fontSize={"small"}>{props.nft?.name}</Box>
                  <Text color={colors.primary2} fontWeight="700" fontSize="20px">
                    {props.nft?.metadata?.name}
                  </Text>
                </Box>

                <Flex flexDirection="column" alignItems="flex-end">
                  <Text color={colors.primary2} fontWeight="500">
                    {t("Price")}
                  </Text>
                  <Flex marginTop="4px">
                    <Image src={token?.logoUrl} height="20px" width="20px" marginRight="4px" />
                    <Text fontWeight="700" color={colors.primary}>
                      {price &&
                        token &&
                        formatNumber(new BigNumber(price).div(Math.pow(10, token.decimals)).toNumber())}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>

            {props.paymentStrategy === PAYMENT_STRATEGY.IN_FOUR && (
              <>
                <Divider />

                <Stack alignItems="center" spacing={4}>
                  {new Array(paymentPeriods).fill(null).map((_, period) => {
                    const principal = Math.ceil(new BigNumber(price).div(4).toNumber());
                    const periodPrice = token
                      ? Math.min(
                          principal,
                          new BigNumber(price).minus(new BigNumber(period).multipliedBy(principal)).toNumber()
                        )
                      : 0;

                    return (
                      <Fragment key={`period-${period}`}>
                        <Flex flex={1} alignItems={"center"} justifyContent={"space-between"} w="full">
                          <HStack>
                            <Text
                              fontSize={"md"}
                              fontWeight={500}
                              color={period === 0 ? "#000" : "#000"}
                              textAlign={"center"}
                            >
                              {period === 0
                                ? t("Initial payment")
                                : `${t("On")} ${format(addWeeks(new Date(), period * 2), "dd/MM/yyyy")}`}
                            </Text>
                          </HStack>
                          <Flex borderRadius="lg" alignItems={"center"} justifyContent={"center"}>
                            <Image src={token?.logoUrl} height="20px" width="20px" marginRight="4px" />
                            <Text
                              fontWeight={period === 0 ? 700 : 500}
                              color={period === 0 ? colors.primary : "#a6a6a6"}
                            >
                              {token &&
                                formatNumber(new BigNumber(periodPrice).div(Math.pow(10, token?.decimals)).toNumber())}
                            </Text>
                          </Flex>
                        </Flex>
                        {/* {period !== paymentPeriods - 1 && <ArrowRightIcon color={"#a6a6a6"} fontSize={"10px"} />} */}
                      </Fragment>
                    );
                  })}
                </Stack>
              </>
            )}

            <Divider />

            <HStack justifyContent="flex-end">
              <Button colorScheme="blue" alignSelf={"center"} onClick={props.onConfirm} width={"120px"}>
                {t("Pay")}
              </Button>
            </HStack>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});
